import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, Button, Card, Col, Descriptions, Empty, Popconfirm, Row, Space, Spin, Tooltip, Typography, message,
} from 'antd';
import { CaretRightOutlined, CodeOutlined, EyeOutlined, PoweroffOutlined, QuestionCircleOutlined, ReloadOutlined, SyncOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '../../../shared/i18n';
import PageHeader from '../../../shared/components/PageHeader';
import { userPath } from '../../../shared/config/paths';
import { dedicatedUserApi as api, userTools, isOk, errMsg, arr, DC_LABEL, CYCLE_LABEL } from '../../../shared/services/dedicatedService';
import DedicatedServerTools from '../../../shared/components/DedicatedServerTools';
import { DedicatedStatusTag } from './MyDedicated';

const { Text } = Typography;

/** Upstream payloads vary: find the first https URL anywhere in a response (KVM launch). */
const findUrl = (v) => {
    if (typeof v === 'string') return /^https:\/\//.test(v) ? v : null;
    if (v && typeof v === 'object') for (const x of Object.values(v)) { const u = findUrl(x); if (u) return u; }
    return null;
};
const RootLogin = ({ id, user }) => {
    const { t } = useTranslation();
    const [pw, setPw] = useState(null);
    const reveal = async () => {
        const r = await api.password(id);
        if (isOk(r)) setPw(r.data?.password || '');
        else message.error(errMsg(r, t('Failed to load')));
    };
    return (
        <Space wrap>
            <Text copyable style={{ fontFamily: 'monospace' }}>{user || 'root'}</Text>
            {user && user !== 'root' && (
                <Tooltip title={t('Not root: switch with sudo -i or su - (same password)')}><QuestionCircleOutlined /></Tooltip>
            )}
            {pw === null
                ? <Button size="small" icon={<EyeOutlined />} onClick={reveal}>{t('Show password')}</Button>
                : <Text copyable style={{ fontFamily: 'monospace' }}>{pw}</Text>}
        </Space>
    );
};

const DedicatedDetail = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();
    const [s, setS] = useState(null);
    const [busy, setBusy] = useState('');
    const tools = useMemo(() => userTools(id), [id]);

    const load = useCallback(async () => {
        const r = await api.get(id);
        if (isOk(r)) setS(r.data);
        else { message.error(errMsg(r, t('Failed to load'))); setS(false); }
    }, [id, t]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (!s || !['ordering', 'provisioning'].includes(s.status)) return undefined;
        const h = setTimeout(load, 30000);
        return () => clearTimeout(h);
    }, [s, load]);

    const power = async (action) => {
        setBusy(action);
        const r = await api.power(id, action);
        setBusy('');
        if (isOk(r)) { message.success(t('Done')); setTimeout(load, 3000); } else message.error(errMsg(r, t('Operation failed')));
    };
    const kvm = async () => {
        setBusy('kvm');
        const r = await api.kvm(id);
        setBusy('');
        const url = isOk(r) ? findUrl(r.data) : null;
        if (url) window.open(url, '_blank', 'noopener');
        else message.error(errMsg(r, t('Operation failed')));
    };

    if (s === null) return <div style={{ textAlign: 'center', padding: 80 }}><Spin /></div>;
    if (s === false) return <Empty description={t('Server not found')} />;
    const live = s.status === 'active';
    const can = (c) => arr(s.capabilities).includes(c);
    const pending = ['awaiting_payment', 'ordering', 'provisioning'].includes(s.status);

    return (
        <div>
            <PageHeader
                title={<Space>{s.hostname}<DedicatedStatusTag status={s.status} /></Space>}
                onBack={() => navigate(userPath('/dedicated'))}
                extra={<>
                    <Button icon={<SyncOutlined />} onClick={load} />
                    {live && <>
                        {can('kvm') && <Button icon={<CodeOutlined />} loading={busy === 'kvm'} onClick={kvm} disabled={!s.kvm}>{t('KVM console')}</Button>}
                        {can('power') && <Button icon={<CaretRightOutlined />} loading={busy === 'on'} onClick={() => power('on')}>{t('Power on')}</Button>}
                        {can('reboot') && (
                            <Popconfirm title={t('Reboot the server?')} onConfirm={() => power('reboot')}>
                                <Button icon={<ReloadOutlined />} loading={busy === 'reboot'}>{t('Reboot')}</Button>
                            </Popconfirm>
                        )}
                        {can('power') && (
                            <Popconfirm title={t('Power off the server?')} onConfirm={() => power('off')}>
                                <Button danger icon={<PoweroffOutlined />} loading={busy === 'off'}>{t('Power off')}</Button>
                            </Popconfirm>
                        )}
                    </>}
                </>}
            />
            {pending && <Alert style={{ marginBottom: 16 }} type="info" showIcon
                message={s.status === 'awaiting_payment' ? t('Waiting for payment') : t('Your server is being set up. We will notify you when it is ready.')} />}
            {s.status === 'failed' && <Alert style={{ marginBottom: 16 }} type="warning" showIcon message={t('Provisioning ran into a problem; our team has been notified and will contact you.')} />}
            {s.status === 'suspended' && <Alert style={{ marginBottom: 16 }} type="warning" showIcon message={t('This server is suspended; renew it to continue')} />}
            {s.upstream_error && <Alert style={{ marginBottom: 16 }} type="warning" showIcon message={t('Live server details are temporarily unavailable')} />}

            <Row gutter={[16, 16]}>
                <Col xs={24} lg={12}>
                    <Card size="small" title={t('Overview')}>
                        <Descriptions column={1} size="small">
                            <Descriptions.Item label={t('Server')}>{s.name}</Descriptions.Item>
                            <Descriptions.Item label={t('Location')}>{t(DC_LABEL[s.data_center] || s.data_center)}</Descriptions.Item>
                            <Descriptions.Item label={t('IP address')}>{s.ip ? <Text copyable style={{ fontFamily: 'monospace' }}>{s.ip}</Text> : '-'}</Descriptions.Item>
                            {s.has_password && <Descriptions.Item label={t('Login')}><RootLogin id={s.id} user={s.root_user} /></Descriptions.Item>}
                            <Descriptions.Item label={t('Billing cycle')}>{t(CYCLE_LABEL[s.billing_cycle] || s.billing_cycle)}</Descriptions.Item>
                            <Descriptions.Item label={t('Expires')}>{s.subscription?.expiry_date ? String(s.subscription.expiry_date).slice(0, 10) : '-'}</Descriptions.Item>
                        </Descriptions>
                    </Card>
                </Col>
                <Col xs={24} lg={12}>
                    <Card size="small" title={t('Hardware')}>
                        <Descriptions column={1} size="small">
                            {s.hardware?.cpu && <Descriptions.Item label="CPU">{s.hardware.cpu}</Descriptions.Item>}
                            {s.hardware?.memory && <Descriptions.Item label={t('Memory')}>{s.hardware.memory}</Descriptions.Item>}
                            {arr(s.hardware?.disks).length > 0 && <Descriptions.Item label={t('Disks')}>{arr(s.hardware.disks).join(' · ')}</Descriptions.Item>}
                            {s.hardware?.port_speed && <Descriptions.Item label={t('Port speed')}>{s.hardware.port_speed}</Descriptions.Item>}
                            {s.hardware?.os && <Descriptions.Item label={t('Operating system')}>{s.hardware.os}</Descriptions.Item>}
                            {arr(s.options).map((o) => <Descriptions.Item key={o.group_id} label={o.group}>{o.addon}</Descriptions.Item>)}
                        </Descriptions>
                    </Card>
                </Col>
            </Row>

            {live && (
                <Card size="small" style={{ marginTop: 16 }}>
                    <DedicatedServerTools tools={tools} caps={s.capabilities} ips={s.ips} usage={s.bandwidth} onChanged={load} />
                </Card>
            )}
        </div>
    );
};

export default DedicatedDetail;
